import { backdropAtom, carInfoModalAtom } from '@/commons/constants';
import { Button, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ClearIcon } from '@mui/x-date-pickers/icons';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { PlanCar } from '@/types/plan';
import * as _ from 'lodash';
import { planCarListAtom } from '@/interfaces/plan/manage';
import { CustomTextField } from '@/components/exec/common/CustomMui';
import CarInfo from './CarInfo';
import { regionAtom } from '@/interfaces/region';
import { RegionGroup } from '@/types/regions';

const MainWrap = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  overflow:hidden;
  gap: 0.5rem;
  .filterRow {
    width: 100%;
    height: 4rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }
  .listBody{
    width: 100%;
    height: 100%;
    flex-grow:1;
    overflow:hidden;
  }
`;

export default function Car() {
  const setBackdrop = useSetAtom(backdropAtom);
  const { data: carList } = useAtomValue(planCarListAtom);
  const { data: regionList } = useAtomValue(regionAtom);

  const setCarInfoModal = useSetAtom(carInfoModalAtom);
  const [contents, setContents] = useState<any[]>();

  const [checkedList, setCheckedList] = useState<any[]>([]);
  const [modifyList, setModifyList] = useState<PlanCar[]>([]);

  const [filterName, setFilterName] = useState<String>();
  const [filterRegion, setFilterRegion] = useState<any>();

  const column: GridColDef[] = [
    { field: 'name', headerName: '차량명', flex: 1, headerAlign: 'center' },
    { field: 'region', headerName: '권역', width: 120, headerAlign: 'center', align: 'center' },
    { field: 'v_work_time', headerName: '운행시간', width: 120, headerAlign: 'center', align: 'center' },
    {
      field: 'lunch_time',
      headerName: '점심시간',
      width: 80,
      headerAlign: 'center',
      align: 'center',
      renderCell: (param?: GridRenderCellParams<any, string[]>) => {
        return (
          <div
            style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            {param.value === null || param.value[0] === param.value[1] ? (
              <ClearIcon htmlColor="#d77" />
            ) : (
              <DoneAllIcon htmlColor="#7a7" />
            )}
          </div>
        );
      },
    },
    {
      field: 'start_type',
      headerName: '출발지',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (value?: number) => {
        switch (value) {
          case 1:
            return '차고';
          case 0:
          default:
            return '센터';
        }
      },
    },
    {
      field: 'end_type',
      headerName: '종착지',
      width: 110,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (value?: number) => {
        switch (value) {
          case 0:
            return '센터';
          case 1:
            return '마지막 배송지';
          case 2:
            return '차고';
          default:
            return '-';
        }
      },
    },
    { field: 'v_volume', headerName: '체적', width: 80, headerAlign: 'center', align: 'center' },
    { field: 'center_name', headerName: '센터', width: 100, headerAlign: 'center', align: 'center' },
    { field: 'center_address', headerName: '센터 주소', width: 240, headerAlign: 'center', align: 'center' },
    { field: 'cargo_address', headerName: '차고 주소', width: 240, headerAlign: 'center', align: 'center' },
  ];

  useEffect(() => {
    if (carList && carList.length > 0) {
      let filterList = carList;
      if (filterName && filterName !== null) {
        filterList = _.filter(filterList, function (o) {
          return o.name.indexOf(filterName) > -1;
        });
      }
      if (filterRegion && filterRegion !== null) {
        filterList = _.filter(filterList, ['region', filterRegion]);
      }
      const temp = _.sortBy(filterList, 'name').map((item: PlanCar) => {
        return {
          ...item,
          id: item.seq,
          v_work_time: item.start_time + ' ~ ' + item.end_time,
          v_volume: item.min_volume + ' ~ ' + item.max_volume,
        };
      });
      setContents(temp);
    }
    setBackdrop(false);
  }, [carList, filterName, filterRegion]);

  const handleSelectionModelChange = (val: any) => {
    const temp = val.map((item: number) => {
      return _.find(contents, ['id', item]);
    });
    setCheckedList(temp);
  };

  const handleRowClick = (item: PlanCar) => {
    setModifyList([item]);
    setCarInfoModal(true);
  };

  const handleSelectModifyClick = () => {
    setModifyList(checkedList);
    setCarInfoModal(true);
  };

  return (
    <>
      <MainWrap>
        <div className="filterRow">
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <CustomTextField
              size="small"
              label="차량명"
              variant="outlined"
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setFilterName(e.target.value)}
            />
            {regionList && (
              <FormControl size="small" sx={{ width: '10rem' }}>
                <InputLabel id="demo-simple-select-label">권역</InputLabel>
                <Select size="small" label="권역" onChange={(e) => setFilterRegion(e.target.value)}>
                  <MenuItem value={null}>선택안함</MenuItem>
                  <MenuItem value={'-'}>-</MenuItem>
                  {regionList.map((item: RegionGroup, index: number) => (
                    <MenuItem key={index} value={item.name}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>
          <Button
            disabled={checkedList.length === 0}
            size="large"
            variant="contained"
            color="info"
            sx={{ width: '6rem' }}
            onClick={handleSelectModifyClick}
          >
            선택수정
          </Button>
        </div>
        <div className="listBody">
          <DataGrid
            checkboxSelection
            disableColumnResize
            disableRowSelectionOnClick
            columns={column}
            onRowClick={(e) => handleRowClick(e.row)}
            rows={contents ?? []}
            onRowSelectionModelChange={(val) => handleSelectionModelChange(val)}
          />
        </div>
      </MainWrap>
      <CarInfo target={modifyList} />
    </>
  );
}
